/**
 * Axios Instance with Authentication Interceptor
 * 
 * This module provides a pre-configured axios instance that automatically
 * adds authentication headers to all requests. This ensures Supabase users
 * (who don't have tokens in localStorage) can make authenticated API calls.
 * 
 * Usage:
 *   import api from '../lib/api';
 *   
 *   // Instead of:
 *   const token = localStorage.getItem('auth_token');
 *   await axios.get(`${API}/endpoint`, { headers: { Authorization: `Bearer ${token}` } });
 *   
 *   // Use:
 *   await api.get('/endpoint');  // Auth headers added automatically!
 */

import axios from 'axios';
import { getApiBaseUrl } from '../config/api';
import { supabase } from './supabase';

// Default timeout for all requests (10 seconds)
const DEFAULT_TIMEOUT = 10000;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: `${getApiBaseUrl()}/api`,
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - adds auth token to every request
api.interceptors.request.use(
  async (config) => {
    try {
      // Get the current Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
    } catch (error) {
      console.log('[API] Error getting auth session:', error.message);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handles common error cases
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle timeout - don't retry
    if (error.code === 'ECONNABORTED') {
      console.log('[API] Request timed out');
      return Promise.reject(error);
    }
    
    // Handle 401 Unauthorized - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh the session
        const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
        
        if (session?.access_token && !refreshError) {
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.log('[API] Token refresh failed:', refreshError.message);
      }
    }
    
    return Promise.reject(error);
  }
);

// Export both the configured instance and a method to make unauthenticated requests
export default api;

// For requests that should NOT include auth (public endpoints)
export const publicApi = axios.create({
  baseURL: `${getApiBaseUrl()}/api`,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});
